// src/components/admin/sections/AdminHero.tsx - VERSIÓN PROFESIONAL CON EDITOR
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { HeroData } from '../../../service/api_elcristo';
import './AdminHero.css';

const AdminHero: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState<'hero' | 'background' | null>(null);
  const titleEditorRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<HeroData>({
    title: '',
    subtitle: '',
    description: '',
    primaryButtonText: '',
    primaryButtonLink: '',
    secondaryButtonText: '',
    secondaryButtonLink: '',
    heroImage: '',
    heroBackgroundImage: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Actualizar contenido del editor cuando cambia formData.title
    if (titleEditorRef.current && formData.title) {
      titleEditorRef.current.innerHTML = formData.title;
    }
  }, [formData.title]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getHeroData();
      console.log('📥 Hero data cargada:', data);
      setFormData(data);
    } catch (error) {
      console.error('Error loading hero data:', error);
      setMessage({ type: 'error', text: 'Error al cargar los datos' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setFormData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const applyTitleFormat = (tag: string) => {
    if (titleEditorRef.current) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const selectedText = range.toString();

      if (selectedText) {
        const span = document.createElement('span');
        span.className = tag;
        span.textContent = selectedText;
        range.deleteContents();
        range.insertNode(span);
        
        // Actualizar formData
        setFormData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearTitleFormat = () => {
    if (titleEditorRef.current) {
      const plainText = titleEditorRef.current.innerText;
      titleEditorRef.current.innerHTML = plainText;
      setFormData(prev => ({ ...prev, title: plainText }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'hero' | 'background') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo se permiten PNG, JPG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ La imagen debe ser menor a 5MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(type);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'hero');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Error al subir imagen');
      }
      
      const data = await response.json();
      
      console.log('✅ Imagen subida:', data.url);
      
      setFormData(prev => ({
        ...prev,
        [type === 'hero' ? 'heroImage' : 'heroBackgroundImage']: data.url
      }));
      
    } catch (err: any) {
      console.error('❌ Error upload:', err);
      setMessage({ type: 'error', text: `❌ ${err.message || 'Error al subir imagen'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Enviando hero data:', formData);
      
      await apiElCristo.updateSiteContent({ hero: formData });
      
      setMessage({ type: 'success', text: '✅ Hero actualizado correctamente' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-hero-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando Hero Section...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-hero-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-home"></i> Hero Section</h1>
          <p>Primera sección visible de la página principal</p>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-heading"></i> Contenido Textual</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                placeholder="Ej: Bienvenidos al"
                className="form-control"
              />
              <small className="hint">
                <i className="fas fa-info-circle"></i> Texto pequeño que aparece sobre el título
              </small>
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título Principal</label>
              
              {/* Toolbar de formato */}
              <div className="editor-toolbar">
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => applyTitleFormat('elcristo-primary')}
                  title="Texto en rojo"
                >
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i>
                  Rojo
                </button>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => applyTitleFormat('elcristo-blue')}
                  title="Texto en azul"
                >
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i>
                  Azul
                </button>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={clearTitleFormat}
                  title="Limpiar formato"
                >
                  <i className="fas fa-eraser"></i>
                  Limpiar
                </button>
              </div>

              {/* Editor contentEditable */}
              <div
                ref={titleEditorRef}
                className="rich-editor"
                contentEditable
                onInput={handleTitleInput}
                suppressContentEditableWarning
                data-placeholder="Ej: Año escolar 2025"
              />

              <small className="hint">
                <i className="fas fa-magic"></i> Selecciona texto y usa los botones de arriba para aplicar colores
              </small>
            </div>

            <div className="form-group">
              <label><i className="fas fa-align-left"></i> Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descripción breve que aparece bajo el título"
                rows={5}
                className="form-control"
                required
              />
              <small className="hint">
                <i className="fas fa-info-circle"></i> Máximo 2-3 líneas recomendadas
              </small>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-mouse-pointer"></i> Botones de Acción (CTA)</h3>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label><i className="fas fa-hand-pointer"></i> Botón Principal - Texto</label>
                <input
                  type="text"
                  name="primaryButtonText"
                  value={formData.primaryButtonText}
                  onChange={handleChange}
                  placeholder="Ej: Conócenos"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label><i className="fas fa-link"></i> Botón Principal - URL</label>
                <input
                  type="text"
                  name="primaryButtonLink"
                  value={formData.primaryButtonLink}
                  onChange={handleChange}
                  placeholder="Ej: #nosotros o /admision"
                  className="form-control"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><i className="fas fa-hand-pointer"></i> Botón Secundario - Texto</label>
                <input
                  type="text"
                  name="secondaryButtonText"
                  value={formData.secondaryButtonText}
                  onChange={handleChange}
                  placeholder="Ej: Radio El Cristo (opcional)"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label><i className="fas fa-link"></i> Botón Secundario - URL</label>
                <input
                  type="text"
                  name="secondaryButtonLink"
                  value={formData.secondaryButtonLink}
                  onChange={handleChange}
                  placeholder="Ej: ./radio (opcional)"
                  className="form-control"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-image"></i> Imágenes</h3>
          </div>
          <div className="card-body">
            <div className="images-grid">
              {/* Imagen Hero */}
              <div className="image-upload-card">
                <div className="image-upload-header">
                  <i className="fas fa-image"></i>
                  <h4>Imagen Principal</h4>
                </div>
                
                {formData.heroImage && (
                  <div className="image-preview-large">
                    <img 
                      src={formData.heroImage.startsWith('http') 
                        ? formData.heroImage 
                        : `${window.location.origin}${formData.heroImage}`
                      } 
                      alt="Hero" 
                    />
                  </div>
                )}
                
                <input
                  type="text"
                  value={formData.heroImage}
                  onChange={(e) => setFormData(prev => ({ ...prev, heroImage: e.target.value }))}
                  placeholder="/assets/kidsa/assets/img/hero/hero-shape.png"
                  className="form-control"
                  style={{ marginBottom: '10px' }}
                />
                
                <button
                  type="button"
                  className="btn btn-upload-full"
                  disabled={uploadingImage === 'hero'}
                  onClick={() => document.getElementById('hero-image-input')?.click()}
                >
                  {uploadingImage === 'hero' ? (
                    <><div className="spinner-small"></div> Subiendo...</>
                  ) : (
                    <><i className="fas fa-cloud-upload-alt"></i> Subir Imagen Principal</>
                  )}
                </button>
                <input
                  id="hero-image-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={(e) => handleImageUpload(e, 'hero')}
                  style={{ display: 'none' }}
                />
                
                <small className="hint">
                  <i className="fas fa-info-circle"></i> PNG, JPG, WEBP (máx 5MB)
                </small>
              </div>

              {/* Imagen Background */}
              <div className="image-upload-card">
                <div className="image-upload-header">
                  <i className="fas fa-mountain"></i>
                  <h4>Imagen de Fondo</h4>
                </div>
                
                {formData.heroBackgroundImage && (
                  <div className="image-preview-large">
                    <img 
                      src={formData.heroBackgroundImage.startsWith('http') 
                        ? formData.heroBackgroundImage 
                        : `${window.location.origin}${formData.heroBackgroundImage}`
                      } 
                      alt="Background" 
                    />
                  </div>
                )}
                
                <input
                  type="text"
                  value={formData.heroBackgroundImage}
                  onChange={(e) => setFormData(prev => ({ ...prev, heroBackgroundImage: e.target.value }))}
                  placeholder="/assets/kidsa/assets/img/hero/bottom.png"
                  className="form-control"
                  style={{ marginBottom: '10px' }}
                />
                
                <button
                  type="button"
                  className="btn btn-upload-full"
                  disabled={uploadingImage === 'background'}
                  onClick={() => document.getElementById('bg-image-input')?.click()}
                >
                  {uploadingImage === 'background' ? (
                    <><div className="spinner-small"></div> Subiendo...</>
                  ) : (
                    <><i className="fas fa-cloud-upload-alt"></i> Subir Imagen de Fondo</>
                  )}
                </button>
                <input
                  id="bg-image-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={(e) => handleImageUpload(e, 'background')}
                  style={{ display: 'none' }}
                />
                
                <small className="hint">
                  <i className="fas fa-info-circle"></i> PNG, JPG, WEBP (máx 5MB)
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadData}
            disabled={saving}
          >
            <i className="fas fa-undo"></i>
            Recargar
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || uploadingImage !== null}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Preview - SIN CARD, FULLWIDTH */}
      <div className="hero-preview-section">
        <div className="hero-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="hero-preview">
          <div className="hero-preview-content">
            <p className="hero-preview-subtitle">{formData.subtitle || 'Subtítulo'}</p>
            <h1 
              className="hero-preview-title" 
              dangerouslySetInnerHTML={{ 
                __html: formData.title || 'Título <span class="elcristo-primary">Principal</span>' 
              }}
            />
            <p className="hero-preview-description">
              {formData.description || 'Descripción del hero section...'}
            </p>
            <div className="hero-preview-buttons">
              <button className="btn-preview-primary">
                {formData.primaryButtonText || 'Botón Principal'}
              </button>
              {formData.secondaryButtonText && (
                <button className="btn-preview-secondary">
                  {formData.secondaryButtonText}
                </button>
              )}
            </div>
          </div>
          
          {formData.heroImage && (
            <div className="hero-preview-image">
              <img 
                src={formData.heroImage.startsWith('http') 
                  ? formData.heroImage 
                  : `${window.location.origin}${formData.heroImage}`
                } 
                alt="Hero preview" 
              />
            </div>
          )}
        </div>
        
        {formData.heroBackgroundImage && (
          <div className="hero-preview-bg-note">
            <i className="fas fa-layer-group"></i>
            Imagen de fondo: <code>{formData.heroBackgroundImage.split('/').pop()}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHero;